import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';
import { ArrowUpRight, Maximize2 } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { addUtmToContentLinks } from '~/modules/tracking/util/add-utm-to-content-links';
import { useProfessionalLinkTracking } from '../hooks/use-professional-link-tracking';
import { useCurrentTabBannerImage } from '@modules/professional/hook/use-current-tab-banner-image';
import type { CompanyImage } from '@modules/professional/type/company-images';
import {
    DetailImageOverlay,
    RICH_TEXT_HTML_CLASS,
    splitLeadHtml,
    TrackedWebsiteCta,
    type OverlaySlide,
} from '~/ui-rocco/pages/detail-shared';

/** The frame is half the row, so the image is asked for at half the anchor. */
const FRAME_SIZES = getImagesSizes({ desktop: 960, mobile: 414 });

/** The project frame's controls: a white pill and a white disc, lifted. */
const FRAME_CONTROL = cn(
    'pointer-events-auto inline-flex items-center bg-white text-[#181818] no-underline',
    'font-sans font-medium transition-colors hover:bg-white/90',
    '[box-shadow:0_2px_8px_0_rgba(0,0,0,0.08),0_8px_24px_0_rgba(0,0,0,0.06)]',
);

/** The prose's type, as the project sets it: 20px, 400, leading 1.3. */
const PROSE_CLASS =
    'leading-[1.3] font-normal text-[#181818] max-md:text-base md:text-[1.25rem]';

/**
 * The practice's picture beside the copy: the first of its company photos,
 * or a project's where it has none. Opens the viewer over every company
 * photo; the website pill rides the frame as it does on the project.
 */
const AboutFrame = ({
    images,
    fallbackSrcs,
    bannerSrc,
}: {
    images: CompanyImage[];
    /** The practice's project photographs, where it has no photos of its own. */
    fallbackSrcs: string[];
    /** The About banner set in Business Manager, which outranks both. */
    bannerSrc?: string | null;
}) => {
    const professional = useProfessionalInitialRouteData();
    const [index, setIndex] = useState<number | null>(null);
    const hasOwnPhotos = images.length > 0;
    // The banner is the one picture an editor chose for this tab, so it leads
    // the frame; the photo sets stay the viewer's contents either way.
    const src = bannerSrc ?? images[0]?.src ?? fallbackSrcs[0];

    // The viewer pages through the practice's own photos, or through its
    // projects' where it has none — the frame always has somewhere to open.
    const slides = useMemo<OverlaySlide[]>(
        () =>
            hasOwnPhotos
                ? images.map((image) => ({
                      src: image.src,
                      alt: image.caption || professional.Title,
                      // A save from the viewer pins this photograph. The
                      // fallback branch below deliberately does not: a
                      // project's picture there stands for the practice.
                      id: image.ID,
                      ...(image.caption
                          ? { credit: { title: image.caption } }
                          : undefined),
                  }))
                : fallbackSrcs.map((fallback) => ({
                      src: fallback,
                      alt: professional.Title,
                  })),
        [hasOwnPhotos, images, fallbackSrcs, professional.Title],
    );

    if (!src) return null;

    const website = professional.Website;

    return (
        <div className="w-full max-md:mb-8">
            <div className="group/frame relative aspect-square w-full overflow-hidden rounded-[2rem] bg-black/5">
                {/* The picture itself opens the viewer, as the tiles' do;
                    the disc on it is the same way in, said out loud. */}
                {slides.length > 0 ? (
                    <button
                        type="button"
                        aria-label="View larger"
                        onClick={() => setIndex(0)}
                        className="absolute inset-0 cursor-pointer border-0 bg-transparent p-0"
                    >
                        <Image
                            src={src}
                            alt={images[0]?.caption || professional.Title}
                            fill
                            fit="cover"
                            sizes={FRAME_SIZES}
                            fadeIn={false}
                            className="transition-transform duration-500 ease-out group-hover/frame:scale-[1.03] motion-reduce:transition-none"
                        />
                    </button>
                ) : (
                    <Image
                        src={src}
                        alt={professional.Title}
                        fill
                        fit="cover"
                        sizes={FRAME_SIZES}
                        fadeIn={false}
                    />
                )}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between max-md:p-4 md:p-[1.5rem]">
                    {website ? (
                        <TrackedWebsiteCta
                            asLink
                            website={website}
                            libraryItemID={professional.ID}
                            professionalID={professional.ID}
                            professionalTitle={professional.Title}
                            source="ProfileFrameWebsite"
                            className={cn(
                                FRAME_CONTROL,
                                'gap-[0.5rem] rounded-[var(--radius-btn-l)] max-md:h-11 max-md:rounded-[var(--radius-btn-m)] max-md:px-4 max-md:text-sm md:h-[3.25rem] md:px-[1.25rem] md:text-[1rem]',
                                'opacity-0 transition-opacity duration-200 group-hover/frame:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none',
                            )}
                        >
                            <ArrowUpRight
                                aria-hidden="true"
                                className="shrink-0 max-md:size-4 md:size-[1.125rem]"
                            />
                            Visit site
                        </TrackedWebsiteCta>
                    ) : (
                        <span />
                    )}
                    {slides.length > 0 ? (
                        <button
                            type="button"
                            aria-label="View larger"
                            onClick={() => setIndex(0)}
                            className={cn(
                                FRAME_CONTROL,
                                'group ml-auto cursor-pointer justify-center rounded-[1.375rem] whitespace-nowrap',
                                'max-md:h-11 max-md:rounded-[var(--radius-btn-m)] max-md:px-[0.8125rem] md:h-[3.25rem] md:px-[1.0625rem]',
                            )}
                        >
                            <span
                                className={cn(
                                    'block max-w-0 overflow-hidden opacity-0 transition-all duration-200 ease-out',
                                    'group-hover:max-w-[6.25rem] group-hover:opacity-100',
                                    'group-focus-visible:max-w-[6.25rem] group-focus-visible:opacity-100',
                                    'motion-reduce:transition-none',
                                )}
                            >
                                <span className="block pr-[0.5rem] max-md:text-sm md:text-[1rem]">
                                    View larger
                                </span>
                            </span>
                            <Maximize2 className="shrink-0 max-md:size-4 md:size-[1.125rem]" />
                        </button>
                    ) : null}
                </div>
            </div>

            {index !== null && slides[index] ? (
                <DetailImageOverlay
                    slides={slides}
                    index={index}
                    onIndexChange={setIndex}
                    onClose={() => setIndex(null)}
                    title={professional.Title}
                    // Their own photograph pins as a photo; a project's stands
                    // for the practice, so a Save keeps the practice.
                    pinId={hasOwnPhotos ? images[index]!.ID : professional.ID}
                    pinType={hasOwnPhotos ? 'PhotoPin' : 'ProfessionalPin'}
                />
            ) : null}
        </div>
    );
};

/**
 * About the practice, laid out as About the Project is: the picture on the
 * left, holding its place as the page scrolls, and the copy on the right —
 * the opening sentence as a statement, the rest as prose, and the practice's
 * own extra sections under it. Above the numbered accordion, which keeps
 * the awards, the team and the clients' words.
 */
export const ProfessionalAboutTabSection = () => {
    const trackContentLink = useProfessionalLinkTracking();
    const {
        professionalOverview,
        customTabs,
        companyImages,
        featuredProjects,
        banners,
    } = useLoaderData<typeof loader>();
    const bannerSrc = useCurrentTabBannerImage(banners, true);
    const html = professionalOverview.BlockSummary;
    if (!html) return null;

    // Landscape photos lead so the square frame reads well; the viewer still
    // pages through all of them.
    const images = [...companyImages].sort(
        (a, b) => Number(a.isPortrait) - Number(b.isPortrait),
    ) as CompanyImage[];
    const fallbackSrcs = (featuredProjects.featuredProjectsList ?? [])
        .map((project) => project.images?.[0]?.src)
        .filter((src): src is string => Boolean(src));
    const extras = customTabs
        .filter((tab) => Boolean(tab.content))
        .map((tab) => ({ title: tab.title, content: tab.content as string }));
    const { lead, rest } = splitLeadHtml(html);

    return (
        <section className="bg-ground max-md:px-5 md:px-[3.125rem]">
            <div className="mx-auto w-full max-w-[113.75rem] max-md:pb-4 md:flex md:gap-[6.25rem] md:pt-[5rem] md:pb-[5rem]">
                {/* Sticky under the tab band, so the picture keeps the reader
                    company down a long column of copy. */}
                <div className="md:sticky md:top-[6rem] md:w-[41.95%] md:shrink-0 md:self-start">
                    <AboutFrame
                        images={images}
                        fallbackSrcs={fallbackSrcs}
                        bannerSrc={bannerSrc}
                    />
                </div>
                {/* One delegated handler for every link in the professional's
                    own copy below — the About prose and the extra sections
                    under it. It attaches the reader's id, which is only known
                    in the browser; the UTM parameters are already in the
                    markup. `auxClick` for the middle button, `contextMenu` so
                    Copy link address takes it too. */}
                <div
                    className="w-full font-sans md:mr-[8.45%] md:w-[calc(49.6%-6.25rem)]"
                    onClick={trackContentLink}
                    onAuxClick={trackContentLink}
                    onContextMenu={trackContentLink}
                >
                    {lead ? (
                        <p className="m-0 font-sans leading-[1.25] font-medium tracking-[-0.025em] text-[#323232] max-md:text-[1.5rem] md:text-[2.0625rem]">
                            {lead}
                        </p>
                    ) : null}
                    <div
                        className={cn(
                            PROSE_CLASS,
                            lead && 'max-md:mt-4 md:mt-[5.75rem]',
                            RICH_TEXT_HTML_CLASS,
                        )}
                        dangerouslySetInnerHTML={{
                            __html: addUtmToContentLinks(rest),
                        }}
                    />
                    {extras.length ? (
                        <div className="flex flex-col max-md:mt-8 max-md:gap-8 md:mt-[3.5rem] md:gap-[2.5rem]">
                            {extras.map((extra) => (
                                <div key={extra.title}>
                                    <p className="m-0 font-sans text-[1.125rem] leading-[1.3] font-medium text-[#181818]">
                                        {extra.title}
                                    </p>
                                    <div
                                        className={cn(
                                            'mt-[0.75rem]',
                                            PROSE_CLASS,
                                            RICH_TEXT_HTML_CLASS,
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html: addUtmToContentLinks(
                                                extra.content,
                                            ),
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
};
